/**
 * Recipe <-> default_config serializer pair.
 *
 * These two functions are the SINGLE serializer pair for the recipe boundary
 * (plan: docs/research/template-builder/APPROACH-kimi.md Phase 1 item 2).
 * They kill the create/update payload divergence in BlogTemplatesPage
 * (pre-refactor :585-671), where create wrote link fields only nested while
 * update double-wrote them flat AND nested, and reads used per-field
 * `topConfig.x || config.x` fallbacks.
 *
 * Rules (adjudication R6/R7 + Phase 0 key-drift fix):
 * - Canonical key spellings on write: includesImages / includesList /
 *   includesCallouts / imageArtStyle / maxCalloutsPerArticle.
 * - Legacy aliases are TOLERATED ON READ (canonical first) and STRIPPED ON
 *   WRITE, so a recipe save converges the stored row to one canonical shape.
 *   `imageStyle` in particular must be stripped: the backend reads it BEFORE
 *   `imageArtStyle` (content_runner.py:2547-2549), so a leftover alias would
 *   shadow the canonical value.
 * - FAQ keys (includeFAQ/includesFAQ) are neither read into the recipe nor
 *   written by it — FAQ is forced True in the backend (R7).
 * - Link fields live ONLY inside autoConfig on write; the flat top-level
 *   duplicates the old update path wrote are stripped.
 * - `enhanceFormatting` is not a recipe field (R6); if present on `existing`
 *   it is preserved as an unrelated key (the legacy flow still reads it).
 */

import type { TemplateDefaultConfig } from '@/services/blogTemplatesService';
import type { AutoTemplateFormData } from './AutoTemplateConfigForm';
import { DEFAULT_RECIPE, GenerationRecipe } from './recipeTypes';

/**
 * autoConfig legacy aliases of managed keys, stripped on write so the stored
 * row carries exactly one spelling per field. Maps legacy alias -> canonical.
 */
const STRIPPED_AUTOCONFIG_ALIASES = [
  'includeImages',   // -> includesImages
  'includeList',     // -> includesList
  'includeCallouts', // -> includesCallouts
  'maxCallouts',     // -> maxCalloutsPerArticle (backend alias, content_runner.py:2576-2578)
  'imageStyle',      // -> imageArtStyle (backend reads this FIRST — must not survive)
  'includeFAQ',      // decorative; FAQ forced True backend-side (R7)
  'includesFAQ',     // decorative; FAQ forced True backend-side (R7)
] as const;

/** Flat top-level duplicates the old update path double-wrote. */
const STRIPPED_TOP_LEVEL_DUPLICATES = [
  'linkStrategy',
  'internalLinkCount',
  'externalLinkCount',
] as const;

type RawConfig = Record<string, unknown>;

function asRaw(value: unknown): RawConfig {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as RawConfig)
    : {};
}

/**
 * Read a field with nested-autoConfig-first, flat-fallback, legacy-alias
 * tolerance. Uses nullish coalescing (NOT `||`) so explicit `false` / `0`
 * values in stored rows survive — the old page's `topConfig.x || config.x`
 * pattern silently destroyed `false`.
 */
function readField<T>(
  autoConfig: RawConfig,
  topLevel: RawConfig,
  canonical: string,
  aliases: readonly string[],
  fallback: T,
): T {
  const candidates: unknown[] = [autoConfig[canonical]];
  for (const alias of aliases) candidates.push(autoConfig[alias]);
  candidates.push(topLevel[canonical]);
  for (const alias of aliases) candidates.push(topLevel[alias]);
  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null) return candidate as T;
  }
  return fallback;
}

/**
 * Convert a stored `default_config` (mixed flat/nested, mixed canonical/legacy
 * spellings — real rows vary) into a canonical GenerationRecipe.
 *
 * Read order per field: autoConfig[canonical] -> autoConfig[legacy aliases]
 * -> top-level[canonical] -> top-level[legacy aliases] -> DEFAULT_RECIPE.
 * This mirrors the backend's own tolerance (content_runner.py AUTO branch:
 * nested-first for links, canonical-first alias reads for booleans) and the
 * old page's flat/nested fallback, minus its `||` false-destroying bug.
 *
 * Missing/empty input yields DEFAULT_RECIPE.
 */
export function defaultConfigToRecipe(
  defaultConfig: TemplateDefaultConfig | undefined | null,
): GenerationRecipe {
  const top = asRaw(defaultConfig);
  const auto = asRaw(top.autoConfig);
  const d = DEFAULT_RECIPE;

  return {
    name: d.name,
    description: d.description,

    // Top-level-managed fields: read top-level first (that's where they live).
    targetWordCount: (top.targetWordCount as number) ?? d.targetWordCount,
    tone: (top.tone as GenerationRecipe['tone']) ?? d.tone,
    languageModel: (top.languageModel as string) ?? d.languageModel,
    imageModel: (top.imageModel as string) ?? d.imageModel,

    // Structure
    minSections: readField(auto, top, 'minSections', [], d.minSections),
    maxSections: readField(auto, top, 'maxSections', [], d.maxSections),
    includesTableOfContents: readField(auto, top, 'includesTableOfContents', [], d.includesTableOfContents),
    includesH3Subsections: readField(auto, top, 'includesH3Subsections', [], d.includesH3Subsections),
    h3Strategy: readField(auto, top, 'h3Strategy', [], d.h3Strategy),
    h3CountPerSection: readField(auto, top, 'h3CountPerSection', [], d.h3CountPerSection),
    h3MinSectionWords: readField(auto, top, 'h3MinSectionWords', [], d.h3MinSectionWords),

    // Components
    includesList: readField(auto, top, 'includesList', ['includeList'], d.includesList),
    listType: readField(auto, top, 'listType', [], d.listType),
    maxListItems: readField(auto, top, 'maxListItems', [], d.maxListItems),
    maxFAQItems: readField(auto, top, 'maxFAQItems', [], d.maxFAQItems),
    includesTables: readField(auto, top, 'includesTables', [], d.includesTables),
    maxTablesPerArticle: readField(auto, top, 'maxTablesPerArticle', [], d.maxTablesPerArticle),
    tableColumns: readField(auto, top, 'tableColumns', [], d.tableColumns),
    tableRows: readField(auto, top, 'tableRows', [], d.tableRows),
    includesCTA: readField(auto, top, 'includesCTA', [], d.includesCTA),
    includesStatsGrid: readField(auto, top, 'includesStatsGrid', [], d.includesStatsGrid),
    includesBlockquote: readField(auto, top, 'includesBlockquote', [], d.includesBlockquote),
    includesCallouts: readField(auto, top, 'includesCallouts', ['includeCallouts'], d.includesCallouts),
    calloutVariants: readField(auto, top, 'calloutVariants', [], d.calloutVariants),
    maxCalloutsPerArticle: readField(auto, top, 'maxCalloutsPerArticle', ['maxCallouts'], d.maxCalloutsPerArticle),

    // Images
    includesImages: readField(auto, top, 'includesImages', ['includeImages'], d.includesImages),
    minImages: readField(auto, top, 'minImages', [], d.minImages),
    maxImages: readField(auto, top, 'maxImages', [], d.maxImages),
    imageArtStyle: readField(auto, top, 'imageArtStyle', ['imageStyle'], d.imageArtStyle),
    imagePosition: readField(auto, top, 'imagePosition', [], d.imagePosition),

    // Links (nested-first matches the backend: content_runner.py:2556-2561)
    linkStrategy: readField(auto, top, 'linkStrategy', [], d.linkStrategy),
    internalLinkCount: readField(auto, top, 'internalLinkCount', [], d.internalLinkCount),
    externalLinkCount: readField(auto, top, 'externalLinkCount', [], d.externalLinkCount),
  };
}

/**
 * Serialize a GenerationRecipe into the ONE canonical default_config shape:
 * - top level: targetWordCount, tone, languageModel/imageModel (when present),
 *   plus autoConfig.
 * - autoConfig: ALL structure/component/image/link fields with canonical
 *   spellings. Link fields are NEVER duplicated flat at top level.
 *
 * When `existing` is provided, unrelated keys are preserved (e.g. `seo_targets`
 * — read by the runner at content_runner.py:3308 — `stylePreset`,
 * `includesSchema`, `enhanceFormatting`) while managed keys and legacy aliases
 * of managed keys are overwritten/stripped, so a recipe save converges the
 * stored row instead of accumulating drift.
 */
export function recipeToDefaultConfig(
  recipe: GenerationRecipe,
  existing?: TemplateDefaultConfig,
): TemplateDefaultConfig {
  const existingTop = asRaw(existing);
  const existingAuto = asRaw(existingTop.autoConfig);

  // Preserve unmanaged existing autoConfig keys (forward-compat), then strip
  // legacy aliases of managed fields so they cannot shadow canonical values.
  const autoConfig: RawConfig = { ...existingAuto };
  for (const alias of STRIPPED_AUTOCONFIG_ALIASES) {
    delete autoConfig[alias];
  }

  // Write the full canonical field set.
  Object.assign(autoConfig, {
    // Structure
    minSections: recipe.minSections,
    maxSections: recipe.maxSections,
    includesTableOfContents: recipe.includesTableOfContents,
    includesH3Subsections: recipe.includesH3Subsections,
    h3Strategy: recipe.h3Strategy,
    h3CountPerSection: recipe.h3CountPerSection,
    h3MinSectionWords: recipe.h3MinSectionWords,
    // Components
    includesList: recipe.includesList,
    listType: recipe.listType,
    maxListItems: recipe.maxListItems,
    maxFAQItems: recipe.maxFAQItems,
    includesTables: recipe.includesTables,
    maxTablesPerArticle: recipe.maxTablesPerArticle,
    tableColumns: recipe.tableColumns,
    tableRows: recipe.tableRows,
    includesCTA: recipe.includesCTA,
    includesStatsGrid: recipe.includesStatsGrid,
    includesBlockquote: recipe.includesBlockquote,
    includesCallouts: recipe.includesCallouts,
    calloutVariants: recipe.calloutVariants,
    maxCalloutsPerArticle: recipe.maxCalloutsPerArticle,
    // Images
    includesImages: recipe.includesImages,
    minImages: recipe.minImages,
    maxImages: recipe.maxImages,
    imageArtStyle: recipe.imageArtStyle,
    imagePosition: recipe.imagePosition,
    // Links (nested only — never duplicated flat)
    linkStrategy: recipe.linkStrategy,
    internalLinkCount: recipe.internalLinkCount,
    externalLinkCount: recipe.externalLinkCount,
  });

  // Preserve unrelated top-level keys; overwrite managed ones.
  const result: RawConfig = { ...existingTop };
  for (const key of STRIPPED_TOP_LEVEL_DUPLICATES) {
    delete result[key];
  }
  result.targetWordCount = recipe.targetWordCount;
  result.tone = recipe.tone;
  if (recipe.languageModel !== undefined) result.languageModel = recipe.languageModel;
  if (recipe.imageModel !== undefined) result.imageModel = recipe.imageModel;
  result.autoConfig = autoConfig;

  return result as TemplateDefaultConfig;
}

/**
 * Map the form's legacy field names onto the canonical GenerationRecipe.
 * (AutoTemplateConfigForm still speaks includeImages/imageStyle; the recipe
 * speaks canonical includesImages/imageArtStyle.)
 */
export function formDataToRecipe(config: AutoTemplateFormData): GenerationRecipe {
  return {
    ...DEFAULT_RECIPE,
    name: config.name,
    description: config.description || '',
    targetWordCount: config.targetWordCount,
    tone: config.tone,
    minSections: config.minSections ?? DEFAULT_RECIPE.minSections,
    maxSections: config.maxSections ?? DEFAULT_RECIPE.maxSections,
    // Components (FAQ is always-on per R7 — includeFAQ is not a recipe field)
    includesList: config.includeList,
    listType: config.listType,
    maxListItems: config.maxListItems ?? DEFAULT_RECIPE.maxListItems,
    maxFAQItems: config.maxFAQItems ?? DEFAULT_RECIPE.maxFAQItems,
    maxCalloutsPerArticle:
      config.maxCalloutsPerArticle ?? DEFAULT_RECIPE.maxCalloutsPerArticle,
    // Images
    includesImages: config.includeImages,
    minImages: config.minImages ?? DEFAULT_RECIPE.minImages,
    maxImages: config.maxImages ?? DEFAULT_RECIPE.maxImages,
    imageArtStyle: config.imageStyle,
    imagePosition: config.imagePosition,
    // Links
    linkStrategy: config.linkStrategy,
    internalLinkCount: config.internalLinkCount,
    externalLinkCount: config.externalLinkCount,
    // Model selection - critical for content generation
    languageModel: config.languageModel,
    imageModel: config.imageModel,
  };
}

/**
 * Inverse of formDataToRecipe: derive the form's initial data from a recipe
 * (used by the inline edit surface in the template detail pane).
 */
export function recipeToFormData(recipe: GenerationRecipe): AutoTemplateFormData {
  return {
    name: recipe.name,
    description: recipe.description,
    targetWordCount: recipe.targetWordCount,
    tone: recipe.tone,
    includeFAQ: true, // always-on per R7; the form still displays the field
    includeImages: recipe.includesImages,
    includeList: recipe.includesList,
    listType: recipe.listType,
    imageStyle: recipe.imageArtStyle,
    minSections: recipe.minSections,
    maxSections: recipe.maxSections,
    minImages: recipe.minImages,
    maxImages: recipe.maxImages,
    imagePosition: recipe.imagePosition,
    linkStrategy: recipe.linkStrategy,
    internalLinkCount: recipe.internalLinkCount,
    externalLinkCount: recipe.externalLinkCount,
    maxFAQItems: recipe.maxFAQItems,
    maxListItems: recipe.maxListItems,
    maxCalloutsPerArticle: recipe.maxCalloutsPerArticle,
    languageModel: recipe.languageModel,
    imageModel: recipe.imageModel,
  };
}
